
import React, { FunctionComponent, Suspense, useMemo } from "react";
import { TruncatedWithTooltip } from "../../../core-components/shared/TruncatedWithTooltip";
import { hours } from "../../../core/utils/smrt-link-date-format";
import { CollectionDataRecord } from "../../../data/model/instrument-model";
import { CollectionPreviewData } from "../../../data/services/run-preview-service";
import { InstrumentType, getMaxZmwsForInstrumentType } from "../../../data/model/instrument-type-model";
import { ZmwsCellPreviewTable } from "./ZmwsCellPreviewTable";
import { numberFormatter } from "src/core/utils/numbers";

const Scatter = React.lazy(async () => {
    const module = await import("react-chartjs-2")
    return { default: module.Scatter }
})

interface Props {
    collection: CollectionDataRecord
    instrumentType: InstrumentType
    previews: CollectionPreviewData[]
    // Shared y-axis max (as an active fraction, 0-1) applied across all wells in a run
    // so the plots can be compared at a glance. Optional; falls back to auto-scaling.
    yAxisMax?: number
}

const PLOT_X_INTERVAL = 60*30 // plot resolution: 30 minutes.

export const ZmwsCellPlot: FunctionComponent<Props> = (props: Props) => {

    const chartOptions = useMemo( () => {
        return {
            scales: {
                yAxes: [{
                    display: true,
                    position: "left",
                    ticks: {
                        min: 0,
                        suggestedMax: props.yAxisMax,
                        callback: value => `${numberFormatter(value*100, 0)}`,
                        fontSize: 10
                    },
                    gridLines: {display:false},
                    scaleLabel: {
                        display: true,
                        labelString: "Active, %"
                    }
                }],
                xAxes: [{
                    display: true,
                    ticks: {
                        min: 0,
                        max: props.collection ? props.collection.movieMinutes * 60 : 0,
                        stepSize: 60 * 60 * 10,
                        callback: value => `${numberFormatter(value/(60*60), 1)}h`,
                        fontSize: 10
                    }
                }]
            },
            tooltips: {
                displayColors: false,
                callbacks: {
                    label: (context) => {
                        const x = context.xLabel
                        const y = context.yLabel
                        return `${hours(x,1)}: ${numberFormatter(100*y, 1)}%`
                    }
                }
            },
            legend: { display: false}
        }
    }, [props.collection, props.yAxisMax])

    const { collection, instrumentType }= props
    if (!collection) {
        return null
    }
    const { sequencingDuration, sequencingZmws } = collection

    const xInterval = sequencingDuration / sequencingZmws.length

    // Divisor that converts a raw active-ZMW count into an active fraction (0-1) for the plot
    const yMax = getMaxZmwsForInstrumentType(instrumentType)
    let data = []
    sequencingZmws.forEach( (val, index) => {
        data.push({
            x: Math.round(index * xInterval),
            y: val / yMax
        })
    })

    const points = []
    let lastX = 0
    for (let point of data) {
        if (point.x >= lastX) {
            points.push(point)
            lastX += PLOT_X_INTERVAL
        }
    }
    if (points[points.length - 1].x !== data[data.length-1].x) {
        points.push(data[data.length-1])
    }

    const chartData = {
        datasets: [{
            data: points,
            pointRadius: 1,
            borderColor: "#00B8C4",
            backgroundColor: "rgba(0, 184, 196, 0.15)",
            pointBackgroundColor: "black",
        }]
    }

    return (
        <div style = {{border: "1px solid #C8C8C8"}} className="pt-1 pb-1 pl-1 pr-1">
            <span style={{fontSize: "0.9em"}}> <strong> {collection.well} </strong> - {collection.state} </span>
            <div style={{fontSize: "0.8em"}}>
                <TruncatedWithTooltip
                    value = {collection.sampleName}
                    maxChars = {20}
                    truncation = "middle"
                />
            </div>
            <div style={{width:250, height: 80, position: "relative"}}>
                <Suspense fallback={<div className="d-flex align-items-center justify-content-center h-100">Loading chart...</div>}>
                    <Scatter
                        width = { 250}
                        height = { 80 }
                        data = {chartData}
                        options={chartOptions}
                    />
                </Suspense>
            </div>
            <div style={{position: "relative"}}>
                <ZmwsCellPreviewTable
                    previews={props.previews}
                    instrumentType={instrumentType}
                />
            </div>
        </div>
    )
}
